import React from 'react';
import { Home, PlusCircle, Truck, MessageSquare } from 'lucide-react';

/**
 * Customer dashboard navigation — mirrors DriverNav (DoneDeal-style):
 *  - Mobile  (<md): sticky bottom tab bar (icon + label)
 *  - Desktop (≥md): horizontal pill bar at the top of the dashboard
 *
 * Props:
 *   active           — current view key
 *   onChange(key)    — fires when user taps a tab
 *   counts           — { deliveries, messages } shown as small pill badges
 */
const TABS = [
  { key: 'home',        label: 'Home',          Icon: Home },
  { key: 'messages',    label: 'Messages',      Icon: MessageSquare, countKey: 'messages' },
  { key: 'post',        label: 'Post Job',      Icon: PlusCircle },
  { key: 'deliveries',  label: 'My Deliveries', Icon: Truck,         countKey: 'deliveries' },
];

export const CustomerNav = ({ active, onChange, counts = {} }) => {
  // Messages badge is rendered with a punchy red colour (not the brand green)
  // so unread messages are unmistakable in the customer's peripheral vision.
  const Badge = ({ n, urgent }) => (
    n != null && n > 0 ? (
      <span className={`absolute -top-1 -right-2 min-w-[18px] h-[18px] px-1 rounded-full text-white text-[10px] font-bold flex items-center justify-center leading-none ring-2 ring-white ${
        urgent ? 'bg-red-500' : 'bg-[#2E7D32]'
      }`}>
        {n > 99 ? '99+' : n}
      </span>
    ) : null
  );

  return (
    <>
      {/* Desktop / tablet: top pill bar */}
      <nav
        className="hidden md:flex items-center gap-1 bg-white border border-gray-200 rounded-full px-2 py-1.5 mb-6 shadow-sm sticky top-2 z-30"
        data-testid="customer-nav-desktop"
      >
        {TABS.map(({ key, label, Icon, countKey }) => {
          const isActive = active === key;
          const n = countKey ? counts[countKey] : undefined;
          const urgent = countKey === 'messages';
          return (
            <button
              key={key}
              type="button"
              onClick={() => onChange(key)}
              className={`relative flex items-center gap-2 px-4 lg:px-5 h-10 rounded-full text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-[#2E7D32] text-white shadow'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
              data-testid={`customer-nav-${key}`}
              aria-current={isActive ? 'page' : undefined}
            >
              <Icon className="w-4 h-4" strokeWidth={2} />
              <span>{label}</span>
              {n != null && n > 0 && (
                <span className={`ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-bold rounded-full ${
                  isActive
                    ? 'bg-white/20 text-white'
                    : urgent
                      ? 'bg-red-500 text-white'
                      : 'bg-gray-100 text-gray-700'
                }`}>{n > 99 ? '99+' : n}</span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Mobile: bottom tab bar — 4 tabs */}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-gray-200 pb-[env(safe-area-inset-bottom)] shadow-[0_-2px_10px_rgba(0,0,0,0.04)]"
        data-testid="customer-nav-mobile"
      >
        <ul className="grid grid-cols-4">
          {TABS.map(({ key, label, Icon, countKey }) => {
            const isActive = active === key;
            const n = countKey ? counts[countKey] : undefined;
            const urgent = countKey === 'messages';
            return (
              <li key={key} className="flex">
                <button
                  type="button"
                  onClick={() => onChange(key)}
                  className={`relative flex-1 flex flex-col items-center justify-center gap-0.5 py-2 min-h-[58px] ${
                    isActive ? 'text-[#2E7D32]' : 'text-gray-500'
                  }`}
                  data-testid={`customer-nav-mobile-${key}`}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <span className="relative">
                    <Icon className="w-[22px] h-[22px]" strokeWidth={isActive ? 2.5 : 1.8} />
                    <Badge n={n} urgent={urgent} />
                  </span>
                  <span className={`text-[11px] leading-none ${isActive ? 'font-bold' : 'font-medium'}`}>{label}</span>
                  {isActive && <span className="absolute top-0 inset-x-6 h-[3px] bg-[#2E7D32] rounded-b-full" />}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
    </>
  );
};

export default CustomerNav;
